// cameraCatalog — the single source of truth for "which cameras are assignable",
// shared by the ⋯ Assign picker (CameraSelectDialog) and the tablet Arrange-mode
// camera tree (CameraTreePanel), so the two can't drift (#86bb5dx62).
//
// Data comes from state.server: dview = a flat `cameras` list indexed 1..bNumCams;
// eview = `allCameras` grouped per server via `camerasByServer` + `authServers`.

// Case-insensitive match on the camera name OR its number. Empty query matches all.
// Mirrors Java's edit-tree filter (CameraTreePanel.java:99, 200ms debounced upstream).
export const matchesSearch = (camera, query) => {
  const q = (query || '').trim().toLowerCase();
  if (!q) return true;
  const name = (camera.sName || '').toLowerCase();
  const num = String(camera.bID);
  return name.includes(q) || num.includes(q);
};

// dview (single NVR): enabled cameras 1..bNumCams that match the query, in order.
export const filterDviewCameras = ({ cameras, originalCameras, bNumCams }, query) => {
  const source = originalCameras || cameras || [];
  const max = bNumCams || 64;
  const out = [];
  for (let i = 1; i <= max; i++) {
    const camera = source.find(c => c.bID === i);
    if (camera && camera.fEnable && matchesSearch(camera, query)) out.push(camera);
  }
  return out;
};

// eview (multi-NVR): [{ server, cameras }] for every server that has at least one
// enabled camera matching the query. Servers with no match are dropped.
export const filterEviewGroups = ({ authServers, camerasByServer }, query) => {
  const servers = authServers || [];
  const byServer = camerasByServer || {};
  const q = (query || '').trim().toLowerCase();
  return servers
    .map(server => {
      // A query that matches the SERVER/system name shows ALL of that server's cameras (David
      // #86bb7phhh): in a long cross-NVR list you usually know the system, not the exact camera, so
      // matching the server name jumps you to it (and `searching` opens the group). Otherwise filter
      // the server's cameras by camera name/number as before.
      const serverMatched = q !== '' && (server.sName || '').toLowerCase().includes(q);
      const cameras = (byServer[server.bSerial] || []).filter(
        c => c.fEnable && (serverMatched || matchesSearch(c, query))
      );
      return { server, cameras };
    })
    .filter(group => group.cameras.length > 0);
};

// A stable key for a camera across dview + eview (eview cams share bID across NVRs).
export const cameraKey = (camera) =>
  camera.compositeKey || `${camera.bSerial || 0}_${camera.bID}`;

// Identity of the CAMERA a slot holds (ignores the per-slot compositeKey, so the
// same camera in two slots — or in the tree vs a slot — compares equal). Used to
// dim tree rows for cameras already placed on the canvas (#86bb5dx62).
export const slotKey = (camera) => `${camera.bSerial || 0}_${camera.bID}`;

// The set of slotKeys currently placed (enabled) in a working camera array.
export const placedSlotKeys = (cameras) => {
  const set = new Set();
  (cameras || []).forEach((c) => { if (c && c.fEnable) set.add(slotKey(c)); });
  return set;
};

// A single empty placeholder slot at absolute index i. Shape mirrors selectView /
// assignCameraToSlot's empty slot (fEnable:false) so the rest of the pipeline
// (buildCameraArrays, saveView) treats it as unfilled.
export const blankSlot = (i, bSerial = 0) => ({
  bID: i + 1,
  bSerial: bSerial || 0,
  sName: `Empty Slot ${i + 1}`,
  fEnable: false,
  compositeKey: `empty_${i + 1}`,
});

// A fresh array of empty placeholder slots — the blank canvas for a New view and
// the tail padding when a page is deleted.
export const blankSlots = (count = 64, bSerial = 0) =>
  Array.from({ length: count }, (_, i) => blankSlot(i, bSerial));
